import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';

import Field from 'Component/Field';
import FIELD_TYPE from 'Component/Field/Field.config';
import FieldPostcodeCity from 'Component/FieldPostcodeCity';
import FieldSiret from 'Component/FieldSiret';
import Loader from 'Component/Loader';
import { CompanyDetailsType } from 'Type/Account.type';
import { RefType } from 'Type/Common.type';
import { ProFieldsType } from 'Type/Config.type';
import { ADDRESS_LINE_MAX_LENGTH, COMPANY_MAX_LENGTH } from 'Util/Customer';
import { VALIDATION_INPUT_TYPE } from 'Util/Validator/Config';

import './MyAccountCompanyForm.style';

/** @namespace PeggysagePwa/Component/MyAccountCompanyForm/Component */
export class MyAccountCompanyFormComponent extends PureComponent {
    static propTypes = {
        formRef: RefType.isRequired,
        taxVatFieldRef: RefType.isRequired,
        addressLinesQty: PropTypes.number.isRequired,
        isSiretEnabled: PropTypes.bool.isRequired,
        defaultCountry: PropTypes.string.isRequired,
        companyDetails: CompanyDetailsType.isRequired,
        onSubmit: PropTypes.func.isRequired,
        isSubmitDisabled: PropTypes.bool.isRequired,
        hasNoSiret: PropTypes.bool.isRequired,
        isStreetAddressExpanded: PropTypes.bool.isRequired,
        countrySpecificProFields: ProFieldsType.isRequired,
        isLoading: PropTypes.bool.isRequired,
        noSiretOptionsList: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.string)).isRequired,
        onNoSiretFieldChange: PropTypes.func.isRequired,
        onToggleStreetAddress: PropTypes.func.isRequired,
        validateZipcodeField: PropTypes.func.isRequired,
        onValidateSiretField: PropTypes.func.isRequired,
        onChangeTaxVatField: PropTypes.func.isRequired
    };

    renderCompanyNameField() {
        const { companyDetails: { societe } = {} } = this.props;

        return (
            <fieldset block="MyAccountCompanyForm" elem="Legend">
                <Field
                  type={ FIELD_TYPE.text }
                  label={ __('Company name') }
                  attr={ {
                      name: 'societe',
                      defaultValue: societe,
                      placeholder: __('Company name')
                  } }
                  addRequiredTag
                  validationRule={ {
                      isRequired: true,
                      inputType: VALIDATION_INPUT_TYPE.noSemiColon,
                      match: (value) => value.length <= COMPANY_MAX_LENGTH,
                      customErrorMessages: {
                          onMatchFail: __('La longueur du nom de la société ne peut pas dépasser %s caractères.', COMPANY_MAX_LENGTH)
                      }
                  } }
                  validateOn={ ['onChange', 'onBlur'] }
                />
            </fieldset>
        );
    }

    renderCountrySpecificProFields() {
        const { countrySpecificProFields } = this.props;

        return countrySpecificProFields.map((field) => (
            <fieldset block="MyAccountCompanyForm" elem="Legend" key={ field.code }>
                { this.renderCountrySpecificProField(field) }
            </fieldset>
        ));
    }

    renderCountrySpecificProField(field) {
        const { type, code } = field;
        const { defaultCountry } = this.props;

        if (code === 'siret' && defaultCountry === 'FR') {
            return (
                <>
                    { this.renderSiretFields() }
                    { this.renderNoSiretReasonField() }
                </>
            );
        }

        switch (type) {
        case 'text':
            return this.renderCountrySpecificProFieldText(field);
        case 'select':
            return this.renderCountrySpecificProFieldSelect(field);
        default:
            return null;
        }
    }

    renderCountrySpecificProFieldText(field) {
        const { code, label, obligatoire } = field;
        const {
            companyDetails: { [code]: defaultValue } = {},
            defaultCountry,
            onChangeTaxVatField,
            taxVatFieldRef
        } = this.props;

        const fieldAttributes = {};

        const validationRule = {
            isRequired: !!obligatoire
        };

        // Prefix for taxvat field
        const defaultValueWithPrefix = code === 'taxvat' && ['BE', 'DE', 'ES'].includes(defaultCountry)
            ? defaultCountry : '';

        if (code === 'taxvat') {
            // Force prefix on change
            fieldAttributes.events = {
                onChange: onChangeTaxVatField
            };
            fieldAttributes.elemRef = taxVatFieldRef;

            // Custom validation rules
            if (defaultCountry === 'BE') {
                validationRule.inputType = VALIDATION_INPUT_TYPE.taxvatBe;
            } else if (defaultCountry === 'ES') {
                validationRule.inputType = VALIDATION_INPUT_TYPE.taxvatEs;
            }
        }

        return (
            <Field
              // eslint-disable-next-line @scandipwa/scandipwa-guidelines/jsx-no-props-destruction
              { ...fieldAttributes }
              type={ FIELD_TYPE.text }
              label={ label }
              attr={ {
                  name: code,
                  defaultValue: defaultValue || defaultValueWithPrefix || ''
              } }
              addRequiredTag={ !!obligatoire }
              validationRule={ validationRule }
              validateOn={ ['onChange', 'onBlur'] }
            />
        );
    }

    renderCountrySpecificProFieldSelect(field) {
        const {
            code, label, obligatoire, options
        } = field;
        const { companyDetails: { [code]: defaultValue } = {} } = this.props;

        const optionsList = options.map(({ value, label }) => ({ id: value, value, label }));

        const defaultValueAttr = optionsList.find(
            (option) => [option.value, option.label].includes(defaultValue)
        )?.value;

        return (
            <Field
              type={ FIELD_TYPE.select }
              label={ label }
              attr={ {
                  name: code,
                  selectPlaceholder: '...',
                  defaultValue: defaultValueAttr
              } }
              options={ optionsList }
              addRequiredTag={ !!obligatoire }
              validationRule={ {
                  isRequired: !!obligatoire
              } }
            />
        );
    }

    renderSiretField() {
        const {
            companyDetails: { siret } = {},
            hasNoSiret,
            onValidateSiretField
        } = this.props;

        return (
            <FieldSiret
              defaultValue={ siret }
              isRequired
              isDisabled={ hasNoSiret }
              mix={ { block: 'MyAccountCompanyForm', elem: 'Field' } }
              fieldsetMix={ { block: 'MyAccountCompanyForm', elem: 'Legend' } }
              onValidateField={ onValidateSiretField }
            />
        );
    }

    renderNoSiretField() {
        const { hasNoSiret, onNoSiretFieldChange } = this.props;

        return (
            <fieldset block="MyAccountCompanyForm" elem="Legend">
                <Field
                  type={ FIELD_TYPE.checkbox }
                  label={ __('I don\'t have a SIRET') }
                  attr={ {
                      id: 'no_siret',
                      name: 'no_siret',
                      value: 'no_siret',
                      defaultChecked: hasNoSiret
                  } }
                  events={ {
                      onChange: onNoSiretFieldChange
                  } }
                />
            </fieldset>
        );
    }

    renderSiretFields() {
        const { isSiretEnabled } = this.props;

        if (!isSiretEnabled) {
            return null;
        }

        return (
            <div block="MyAccountCompanyForm" elem="SiretWrapper">
                { this.renderSiretField() }
                { this.renderNoSiretField() }
            </div>
        );
    }

    renderNoSiretReasonField() {
        const {
            isSiretEnabled,
            hasNoSiret,
            noSiretOptionsList,
            companyDetails: { siret_comment } = {}
        } = this.props;

        if (!isSiretEnabled || !hasNoSiret) {
            return null;
        }

        const defaultValue = noSiretOptionsList.find(
            (option) => [option.value, option.label.toString()].includes(siret_comment)
        )?.value;

        return (
            <>
                <fieldset block="MyAccountCompanyForm" elem="Legend">
                    <Field
                      type={ FIELD_TYPE.select }
                      label={ __('Raison de l\'absence de SIRET') }
                      attr={ {
                          name: 'siret_comment',
                          // noPlaceholder: true
                          // selectPlaceholder: `${__('Raison de l\'absence de SIRET')} *`
                          selectPlaceholder: __('Veuillez choisir une raison...'),
                          defaultValue
                      } }
                      options={ noSiretOptionsList }
                      addRequiredTag
                      validationRule={ {
                          isRequired: true
                      } }
                      validateOn={ ['onChange'] }
                    />
                </fieldset>
                <div block="MyAccountCompanyForm" elem="NoSiretReasonWarning">
                    <p>
                        { __('Les délais d\'ouverture de compte pro seront plus longs') }
                    </p>
                </div>
            </>
        );
    }

    renderStreetFields() {
        const {
            companyDetails: {
                street = []
            } = {},
            addressLinesQty,
            isStreetAddressExpanded,
            onToggleStreetAddress
        } = this.props;

        if (addressLinesQty === 1) {
            return (
                <fieldset block="MyAccountCompanyForm" elem="Legend">
                    <Field
                      type={ FIELD_TYPE.text }
                      label={ __('Street address') }
                      attr={ {
                          name: 'street',
                          defaultValue: street[0] || '',
                          placeholder: __('Street address')
                      } }
                      addRequiredTag
                      validationRule={ {
                          inputType: VALIDATION_INPUT_TYPE.noSemiColon,
                          isRequired: true,
                          match: (value) => value.length <= ADDRESS_LINE_MAX_LENGTH,
                          customErrorMessages: {
                              onMatchFail: __('La longeur de l\'adresse ne peut pas dépasser %s caractères.', ADDRESS_LINE_MAX_LENGTH)
                          }
                      } }
                      validateOn={ ['onChange'] }
                    />
                </fieldset>
            );
        }

        return (
            <div block="MyAccountCompanyForm" elem="StreetWrapper">
                { Array.from(Array(addressLinesQty), (x, i) => (
                    <fieldset
                      block="MyAccountCompanyForm"
                      elem="Legend"
                      mods={ { isVisible: i === 0 || isStreetAddressExpanded } }
                      key={ i }
                    >
                        <Field
                          type={ FIELD_TYPE.text }
                          label={ i === 0 && !isStreetAddressExpanded ? __('Street address')
                              : __('Street address line %s', i + 1) }
                          attr={ {
                              name: `street_${i}`,
                              defaultValue: street[i] || '',
                              placeholder: i === 0 && !isStreetAddressExpanded ? __('Street address')
                                  : __('Street address line %s', i + 1)
                          } }
                          addRequiredTag={ i === 0 }
                          validationRule={ {
                              inputType: VALIDATION_INPUT_TYPE.noSemiColon,
                              isRequired: i === 0,
                              match: (value) => value.length <= ADDRESS_LINE_MAX_LENGTH,
                              customErrorMessages: {
                                  onMatchFail: __('La longeur de l\'adresse ne peut pas dépasser %s caractères.', ADDRESS_LINE_MAX_LENGTH)
                              }
                          } }
                          validateOn={ i === 0 ? ['onChange'] : [] }
                        />
                    </fieldset>
                )) }
                <button
                  type="button"
                  block="MyAccountCompanyForm"
                  elem="StreetToogleButton"
                  mix={ {
                      block: 'Button',
                      mods: { likeLink: true, isVisible: !isStreetAddressExpanded }
                  } }
                  onClick={ onToggleStreetAddress }
                >
                    { __('Ajouter un complément d\'adresse') }
                </button>
            </div>
        );
    }

    renderAddressFields() {
        const {
            companyDetails: {
                postcode,
                city
            } = {},
            validateZipcodeField
        } = this.props;

        return (
            <>
                { this.renderStreetFields() }
                <FieldPostcodeCity defaultValues={ { postcode, city } } validatePostcode={ validateZipcodeField } />
            </>
        );
    }

    renderSubmitButton() {
        const { onSubmit, isSubmitDisabled } = this.props;

        return (
            <div block="MyAccountCompanyForm" elem="Buttons">
                <button
                  block="Button"
                  type="button"
                  mix={ { block: 'MyAccountCompanyForm', elem: 'SubmitButton' } }
                  onClick={ onSubmit }
                  disabled={ isSubmitDisabled }
                >
                    { __('Continue') }
                </button>
            </div>
        );
    }

    renderContent() {
        return (
            <>
                { this.renderCompanyNameField() }
                { this.renderCountrySpecificProFields() }
                { /* this.renderSiretFields() */ }
                { /* this.renderNoSiretReasonField() */ }
                { this.renderAddressFields() }
                { this.renderSubmitButton() }
            </>
        );
    }

    render() {
        const { isLoading, formRef } = this.props;

        return (
            <div block="MyAccountCompanyForm" ref={ formRef }>
                <Loader isLoading={ isLoading } />
                { this.renderContent() }
            </div>
        );
    }
}

export default MyAccountCompanyFormComponent;
